// src/state.ts —— TD-823：UI 共用键沿用原型名；外壳 / 表格 / 系统参数 / dash 原生页切片类型与初值函数集中声明（契约锁与测试宿主直接引用）
// 契约文件：ds 切片（DashSlice / dashDefaults）为协调者授权的唯一增量窗口（SP8b W-0），其余只增不改
import type { Lang } from '../../shared/src/dc';
import type { ConfirmSpec, DrawerSpec, FormDef, FormVals } from './pages/types';
import type { AdminMe } from './perm';

// C-24 导航页全集（原型 21 项 + C-25/26/27/29/30 新增 5 项）
export type Page =
  | 'dashboard' | 'users' | 'cards' | 'cardActions' | 'tx' | 'wallets' | 'review' | 'treasury'
  | 'agents' | 'agentUsers' | 'commissions' | 'agentWd'
  | 'products' | 'settings' | 'tgbot'
  | 'payout' | 'orders' | 'budgets' | 'upstreamCosts' | 'upstream' | 'logs' | 'alerts'
  | 'cms' | 'admins' | 'roles' | 'audit' | 'cryptoPayments' | 'profit' | 'broadcast';

// 原型 navDef（L681）的 21 页，parity 用例的比较范围
export const PROTO_PAGES: readonly Page[] = [
  'dashboard', 'users', 'cards', 'tx', 'wallets', 'review', 'agents', 'agentUsers', 'commissions', 'agentWd',
  'products', 'settings', 'tgbot', 'payout', 'orders', 'upstream', 'logs', 'cms', 'admins', 'roles', 'audit',
];

// booting：启动期会话恢复中（持久 token → /me，或 refresh cookie 试探），不渲染登录页以免闪屏、不受理登录；结束后切 app / login
export type Screen = 'booting' | 'login' | 'onboard' | 'app';

export interface OnbState {
  step: 1 | 2;
  old: string;
  nw: string;
  nw2: string;
  secret: string;
  qr: string;
  code: string;
  err: string;
}

export type BadgeKey = 'review' | 'agentWd' | 'logs' | 'alerts';

export interface ShellSlice {
  me: AdminMe | null;
  loginPwd: string;
  loginOtp: string;
  loginErr: string;
  otpErr: boolean;
  busy: '' | 'login' | 'onb';
  onb: OnbState | null; // 非 null = 受限窗口向导（C-01b）
  badges: Readonly<Record<BadgeKey, number>>;
  timezone: string; // GET /api/public/config → timezone（D21）
  idleLeft: number; // 空闲提示框倒计时秒数；0 = 未打开（2026-10-15 空闲自动退出）
}

export interface TableSlice {
  rev: number;
  drawer: DrawerSpec | null;
  note: string;
  // opts：select 字段的动态选项（FormChange.options 合并；缺省用 FieldDef.options），如上级代理远程搜索（多级代理 2026-10-18）
  form: { def: FormDef; vals: FormVals; err: string; saving: boolean; opts?: Readonly<Record<string, readonly string[]>> } | null;
  confirm: ConfirmSpec | null;
}

export interface SettingsSlice {
  loaded: boolean;
  items: Readonly<Record<string, string>>;
  base: Readonly<Record<string, string>>;
  dirty: boolean;
  invalid: readonly string[];
  err: string;
  warn: string;
  saving: boolean;
  // 有未保存修改时被拦截的目标页（页面内确认框打开中）；null = 无拦截
  leaveTo: Page | null;
  // 「发送测试告警」（06 C-14 ⑦，2026-10-20）：在途标记与最近一次两通道结果（null = 尚未发送 / 已复位）
  alertTest: AlertTestState;
}

export interface AlertTestChannel {
  ok: boolean;
  error: string;
}

export interface AlertTestState {
  busy: boolean;
  result: { telegram: AlertTestChannel; email: AlertTestChannel } | null;
}

// dash 原生页（dashboard / upstream / roles）数据存各子模块 WeakMap<host, {epoch, data}>；本切片只承担重渲染触发（rev 递增）
export interface DashSlice {
  rev: number;
}

export interface UiState {
  lang: Lang;
  screen: Screen;
  page: Page;
  tab: number; // 原型页签下标；原生页按 views[tab].key 取字符串键
  sidebar: boolean;
  adminUser: string; // 原型登录账号键（L37 绑定，保留键名）；进壳后同步为 /me.account（影子实例「本人」判定沿用该键）
  toast: string;
  toastId: number;
}

export interface AdminSlices {
  sh: ShellSlice;
  tb: TableSlice;
  st: SettingsSlice;
  ds: DashSlice;
}

// 影子实例已退场（TD-825）：无字符串索引签名，未声明键写入在编译期报错（admin-final 类型断言锁定）
export type AdminState = UiState & AdminSlices;

export function tableDefaults(): TableSlice {
  return { rev: 0, drawer: null, note: '', form: null, confirm: null };
}

export function settingsDefaults(): SettingsSlice {
  return { loaded: false, items: {}, base: {}, dirty: false, invalid: [], err: '', warn: '', saving: false, leaveTo: null, alertTest: { busy: false, result: null } };
}

export function dashDefaults(): DashSlice {
  return { rev: 0 };
}
